import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { PrescriptionService } from '@services/prescription.service';

interface IMedicamentoReceta {
  nombre: string;
  dosis: string;
  frecuencia: string;
  via: string;
  duracion: string;
  indicaciones: string;
}

interface IRecetaSolicitud {
  id: string;
  tipo: 'receta' | 'laboratorio' | 'gabinete' | 'interno';
  nombre: string;
  fecha_creacion: string;
  ultima_modificacion: string;
  finalizada: boolean;
  origen: string;
  medicamentos?: IMedicamentoReceta[];
  estudios?: any[];
  notas?: string;
  modo_entrada?: 'formulario' | 'texto';
  contenido_html?: string;
  esHistorico?: boolean;
  nota_origen_id?: number;
  backendId?: number;
  medical_record_id?: number;
}

interface IPlantillaReceta {
  id: string;
  nombre: string;
  descripcion?: string;
  medicamentos: IMedicamentoReceta[];
  fecha_creacion: string;
  ultima_modificacion: string;
}

@Component({
  selector: 'app-indicaciones',
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule],
  templateUrl: './indicaciones.component.html',
  styleUrls: ['./indicaciones.component.scss'],
})
export class IndicacionesComponent implements OnInit, OnChanges {
  // ✅ Modo modal - HostBinding para aplicar clase CSS al host
  @Input() isModal: boolean = false;
  @HostBinding('class.modal-mode') get isModalMode() { return this.isModal; }

  @Input() patient: any = null;

  @Input() isLocked: boolean = false;
  @Input() indicacionesMedicas: IRecetaSolicitud[] = [];
  @Input() plantillasRecetas: IPlantillaReceta[] = [];
  @Input() startCollapsed: boolean = false;
  @Input() selectedRecetaId: string | number | null = null;

  @Output() onSave = new EventEmitter<IRecetaSolicitud[]>();
  @Output() onSavePlantilla = new EventEmitter<IPlantillaReceta>();
  @Output() onUpdatePlantilla = new EventEmitter<IPlantillaReceta>();
  @Output() onDeletePlantilla = new EventEmitter<string>();

  // ✅ NUEVO: Output para cerrar modal
  @Output() closeModal = new EventEmitter<void>();

  indicacionesCollapsed: boolean = false;
  overflowIndicacionesOpen: boolean = false;
  activeIndicacionIndex: number | null = null;
  tabsLimit: number = 5;

  // Plantillas
  modalPlantillasAbierto: boolean = false;
  modalFormPlantillaAbierto: boolean = false;
  plantillaEnEdicion: IPlantillaReceta | null = null;
  formPlantilla: { nombre: string; descripcion: string } = { nombre: '', descripcion: '' };

  // Modal de selección de modo de entrada
  modalSeleccionModoAbierto: boolean = false;
  modalConfirmarCambioModoAbierto: boolean = false;

  // Modal de confirmación de eliminación
  modalConfirmarEliminacionAbierto: boolean = false;

  // Configuración de Quill para texto enriquecido
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['clean'],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private prescriptionService: PrescriptionService
  ) {}

  ngOnInit(): void {
    if (this.startCollapsed) {
      this.indicacionesCollapsed = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reaccionar cuando indicacionesMedicas cambia desde el padre
    if (changes['indicacionesMedicas']) {
      const currentValue = changes['indicacionesMedicas'].currentValue;
      const previousValue = changes['indicacionesMedicas'].previousValue;
      const isFirstChange = changes['indicacionesMedicas'].firstChange;

      console.log('[Indicaciones] ngOnChanges - indicacionesMedicas:', {
        length: currentValue?.length || 0,
        previousLength: previousValue?.length || 0,
        firstChange: isFirstChange,
        currentActiveIndex: this.activeIndicacionIndex,
        collapsed: this.indicacionesCollapsed,
      });

      // Log detallado de cada receta
      if (currentValue && currentValue.length > 0) {
        currentValue.forEach((receta: any, i: number) => {
          console.log(`[Indicaciones] Receta ${i}:`, receta?.nombre, '- medicamentos:', receta?.medicamentos?.length || 0, '- finalizada:', receta?.finalizada, '- esHistorico:', receta?.esHistorico, '- id:', receta?.id, '- backendId:', receta?.backendId);
        });

        // Ajustar si el índice actual está fuera de rango
        if (this.activeIndicacionIndex !== null && this.activeIndicacionIndex >= currentValue.length) {
          this.activeIndicacionIndex = null;
          console.log('[Indicaciones] Indice fuera de rango, reseteando a null');
        }

        // Si no hay ninguna seleccionada, seleccionar la primera no histórica
        if (this.activeIndicacionIndex === null) {
          this.seleccionarPrimeraNoHistorica();
        }

        // Forzar deteccion de cambios para asegurar que las tabs se rendericen
        this.cdr.detectChanges();
      } else {
        this.activeIndicacionIndex = null;
      }
    }

    if (changes['startCollapsed'] && changes['startCollapsed'].currentValue === true) {
      this.indicacionesCollapsed = true;
    }

    if ((changes['selectedRecetaId'] || changes['indicacionesMedicas']) && this.selectedRecetaId != null) {
      this.seleccionarRecetaPorId(this.selectedRecetaId);
    }
  }

  private seleccionarRecetaPorId(recetaId: string | number): void {
    if (!this.indicacionesMedicas || this.indicacionesMedicas.length === 0) return;

    const target = String(recetaId);
    const index = this.indicacionesMedicas.findIndex((receta: any) => {
      const backendId = receta?.backendId != null ? String(receta.backendId) : null;
      const id = receta?.id != null ? String(receta.id) : null;
      const idSinPrefijoBackend = id?.startsWith('backend-') ? id.replace('backend-', '') : id;

      return backendId === target || id === target || idSinPrefijoBackend === target;
    });

    if (index !== -1) {
      this.activeIndicacionIndex = index;
      this.cdr.detectChanges();
      console.log('[Indicaciones] Receta seleccionada por ID externo:', recetaId, 'index:', index);
    } else {
      console.log('[Indicaciones] No se encontró receta para ID externo:', recetaId);
    }
  }

  // Selecciona la primera indicación no histórica
  private seleccionarPrimeraNoHistorica(): void {
    if (!this.indicacionesMedicas || this.indicacionesMedicas.length === 0) {
      return;
    }

    const primeraNoHistorica = this.indicacionesMedicas.findIndex(i => !i.esHistorico);
    if (primeraNoHistorica !== -1) {
      this.activeIndicacionIndex = primeraNoHistorica;
      console.log('[Indicaciones] Seleccionada primera no histórica - index:', primeraNoHistorica);
    } else {
      console.log('[Indicaciones] No hay indicaciones no históricas');
    }
  }

  toggleIndicacionesCollapsed(): void {
    this.indicacionesCollapsed = !this.indicacionesCollapsed;
  }

  // ✅ NUEVO: Método para cerrar modal
  onCloseModal(): void {
    if (this.isModal) {
      this.closeModal.emit();
    }
  }

  // ==================== AGREGAR NUEVA RECETA ====================
  iniciarNuevaReceta(): void {
    this.indicacionesCollapsed = false;
    this.modalSeleccionModoAbierto = true;
  }

  private crearRecetaConModo(modo: 'formulario' | 'texto'): void {
    if (this.indicacionesMedicas.length > 0 && this.activeIndicacionIndex !== null && this.activeIndicacionIndex >= 0) {
      const recetaActual = this.indicacionesMedicas[this.activeIndicacionIndex];
      if (!recetaActual.finalizada) {
        recetaActual.finalizada = true;
      }
    }

    const now = new Date();
    const nombre = `R-${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()}`;

    const nuevaReceta: IRecetaSolicitud = {
      id: Date.now().toString(),
      tipo: 'receta',
      nombre,
      fecha_creacion: now.toISOString(),
      ultima_modificacion: now.toISOString(),
      finalizada: false,
      origen: 'manual',
      modo_entrada: modo,
      medicamentos: modo === 'formulario' ? [{ nombre: '', dosis: '', frecuencia: '', via: '', duracion: '', indicaciones: '' }] : [],
      contenido_html: modo === 'texto' ? '' : undefined,
      notas: '',
    };

    this.indicacionesMedicas.push(nuevaReceta);
    this.activeIndicacionIndex = this.indicacionesMedicas.length - 1;

    this.cdr.detectChanges();
    this.emitirCambios();
  }

  agregarNuevaReceta(): void {
    if (this.indicacionesMedicas.length > 0 && this.activeIndicacionIndex !== null && this.activeIndicacionIndex >= 0) {
      const recetaActual = this.indicacionesMedicas[this.activeIndicacionIndex];
      if (!recetaActual.finalizada) {
        recetaActual.finalizada = true;
      }
    }

    const now = new Date();
    const nombre = `R-${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()}`;

    const nuevaReceta: IRecetaSolicitud = {
      id: Date.now().toString(),
      tipo: 'receta',
      nombre,
      fecha_creacion: now.toISOString(),
      ultima_modificacion: now.toISOString(),
      finalizada: false,
      origen: 'manual',
      medicamentos: [],
      notas: '',
    };

    this.indicacionesMedicas.push(nuevaReceta);
    this.activeIndicacionIndex = this.indicacionesMedicas.length - 1;

    // Forzar detección de cambios para que la UI muestre la nueva receta
    this.cdr.detectChanges();

    this.emitirCambios();
  }

  // ==================== AGREGAR MEDICAMENTO ====================
  agregarMedicamento(): void {
    const recetaActiva = this.recetaActual;

    if (!recetaActiva || recetaActiva.finalizada || recetaActiva.esHistorico) {
      return;
    }

    // Si la receta ya tiene un modo definido, agregar directamente
    if (recetaActiva.modo_entrada === 'formulario') {
      this.agregarMedicamentoFormulario();
      return;
    }

    // Si es texto enriquecido, no se pueden agregar más medicamentos
    if (recetaActiva.modo_entrada === 'texto') {
      return;
    }

    // Si no tiene modo definido, mostrar modal de selección
    this.modalSeleccionModoAbierto = true;
  }

  // Modal de selección de modo
  cerrarModalSeleccionModo(): void {
    this.modalSeleccionModoAbierto = false;
  }

  seleccionarModoFormulario(): void {
    const recetaActiva = this.recetaActual;

    if (!recetaActiva || recetaActiva.modo_entrada) {
      this.crearRecetaConModo('formulario');
    } else {
      recetaActiva.modo_entrada = 'formulario';
      this.agregarMedicamentoFormulario();
    }
    this.cerrarModalSeleccionModo();
  }

  seleccionarModoTexto(): void {
    const recetaActiva = this.recetaActual;

    if (!recetaActiva || recetaActiva.modo_entrada) {
      this.crearRecetaConModo('texto');
    } else {
      recetaActiva.modo_entrada = 'texto';
      recetaActiva.contenido_html = '';
      recetaActiva.medicamentos = [];
      recetaActiva.ultima_modificacion = new Date().toISOString();
      this.emitirCambios();
    }
    this.cerrarModalSeleccionModo();
  }

  agregarMedicamentoFormulario(): void {
    const recetaActiva = this.recetaActual;

    if (!recetaActiva || recetaActiva.finalizada) {
      return;
    }

    if (!recetaActiva.medicamentos) {
      recetaActiva.medicamentos = [];
    }

    recetaActiva.medicamentos.push({
      nombre: '',
      dosis: '',
      frecuencia: '',
      via: '',
      duracion: '',
      indicaciones: '',
    });

    recetaActiva.ultima_modificacion = new Date().toISOString();

    this.emitirCambios();
  }

  // Manejar cambios en el editor Quill
  onContenidoHtmlChanged(event: any): void {
    if (event.source === 'user') {
      const recetaActiva = this.recetaActual;
      if (recetaActiva) {
        recetaActiva.contenido_html = event.html || '';
        recetaActiva.ultima_modificacion = new Date().toISOString();
        this.emitirCambios();
      }
    }
  }

  // Cambiar modo de entrada (para permitir cambiar de texto a formulario o viceversa)
  cambiarModoEntrada(): void {
    const recetaActiva = this.recetaActual;
    if (!recetaActiva || recetaActiva.finalizada) return;

    // Verificar si hay contenido
    const tieneContenido =
      (recetaActiva.medicamentos && recetaActiva.medicamentos.length > 0) ||
      (recetaActiva.contenido_html && recetaActiva.contenido_html.trim().length > 0);

    if (tieneContenido) {
      // Mostrar modal de confirmación
      this.modalConfirmarCambioModoAbierto = true;
    } else {
      // Cambiar directamente sin preguntar
      this.ejecutarCambioModo();
    }
  }

  cerrarModalConfirmarCambioModo(): void {
    this.modalConfirmarCambioModoAbierto = false;
  }

  confirmarCambioModo(): void {
    this.modalConfirmarCambioModoAbierto = false;
    this.ejecutarCambioModo();
  }

  private ejecutarCambioModo(): void {
    const recetaActiva = this.recetaActual;
    if (!recetaActiva) return;

    // Resetear el modo para mostrar el modal de selección
    recetaActiva.modo_entrada = undefined;
    recetaActiva.medicamentos = [];
    recetaActiva.contenido_html = '';
    recetaActiva.ultima_modificacion = new Date().toISOString();

    this.emitirCambios();
    this.modalSeleccionModoAbierto = true;
  }

  // ==================== ELIMINAR MEDICAMENTO ====================
  eliminarMedicamento(medicamentoIndex: number): void {
    const recetaActiva = this.recetaActual;

    if (!recetaActiva || recetaActiva.finalizada || !recetaActiva.medicamentos) {
      return;
    }

    recetaActiva.medicamentos.splice(medicamentoIndex, 1);
    recetaActiva.ultima_modificacion = new Date().toISOString();

    this.emitirCambios();
  }

  // ==================== REEMITIR RECETA ====================
  reemitirReceta(): void {
    const recetaAntigua = this.recetaActual;

    if (!recetaAntigua) {
      return;
    }

    const now = new Date();
    const nuevaReceta: IRecetaSolicitud = {
      ...recetaAntigua,
      id: Date.now().toString(),
      nombre: `R-${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()}`,
      fecha_creacion: now.toISOString(),
      ultima_modificacion: now.toISOString(),
      finalizada: false,
      origen: 'reemision',
      medicamentos: recetaAntigua.medicamentos ? [...recetaAntigua.medicamentos] : [],
    };

    this.indicacionesMedicas.push(nuevaReceta);
    this.activeIndicacionIndex = this.indicacionesMedicas.length - 1;

    this.emitirCambios();
  }

  // ==================== GUARDAR RECETA ====================
  guardarReceta(): void {
    const receta = this.recetaActual;

    if (!receta) {
      return;
    }

    receta.ultima_modificacion = new Date().toISOString();
    receta.finalizada = true;

    console.log('[Indicaciones] Guardando receta:', receta);

    this.emitirCambios();
  }

  // ==================== ELIMINAR RECETA ====================
  abrirModalEliminar(): void {
    if (!this.recetaActual) return;
    if (this.recetaActual.esHistorico) {
      console.log('[Indicaciones] No se puede eliminar una receta histórica');
      return;
    }
    this.modalConfirmarEliminacionAbierto = true;
  }

  cerrarModalEliminar(): void {
    this.modalConfirmarEliminacionAbierto = false;
  }

  async confirmarEliminacion(): Promise<void> {
    if (!this.recetaActual) {
      this.cerrarModalEliminar();
      return;
    }

    const recetaAEliminar = this.recetaActual;
    const backendId = recetaAEliminar.backendId;

    console.log('[Indicaciones] confirmarEliminacion() - receta:', recetaAEliminar.nombre, 'backendId:', backendId);

    if (backendId) {
      try {
        console.log('[Indicaciones] Eliminando receta del backend...');
        await this.prescriptionService.delete(backendId);
        console.log('[Indicaciones] Receta eliminada del backend exitosamente');
      } catch (error) {
        console.error('[Indicaciones] Error eliminando receta del backend:', error);
        this.cerrarModalEliminar();
        return;
      }
    }

    const indexEnIndicaciones = this.indicacionesMedicas.findIndex(i => i.id === recetaAEliminar.id);
    if (indexEnIndicaciones !== -1) {
      console.log('[Indicaciones] Eliminando receta del array local - index:', indexEnIndicaciones);
      this.indicacionesMedicas.splice(indexEnIndicaciones, 1);
    }

    this.activeIndicacionIndex = null;
    this.cerrarModalEliminar();
    this.emitirCambios();

    // Seleccionar automáticamente otra receta no histórica
    this.seleccionarPrimeraNoHistorica();

    this.cdr.detectChanges();
  }

  eliminarRecetaActual(): void {
    this.abrirModalEliminar();
  }

  // ==================== IMPRIMIR RECETA ====================
  imprimirReceta(): void {
    const receta = this.recetaActual;

    if (!receta) {
      return;
    }

    console.log('[Indicaciones] Imprimir receta:', receta);
    alert('Funcionalidad de impresión en desarrollo');
  }

  // ==================== ENVIAR POR WHATSAPP ====================
  enviarPorWhatsapp(): void {
    const receta = this.recetaActual;

    if (!receta) {
      return;
    }

    let texto = `*${receta.nombre}*\n\n`;

    if (receta.medicamentos && receta.medicamentos.length > 0) {
      texto += '*Medicamentos:*\n';
      receta.medicamentos.forEach((med: IMedicamentoReceta, index: number) => {
        texto += `${index + 1}. ${med.nombre || 'Sin nombre'}\n`;
        if (med.dosis) texto += `   Dosis: ${med.dosis}\n`;
        if (med.frecuencia) texto += `   Frecuencia: ${med.frecuencia}\n`;
        if (med.via) texto += `   Vía: ${med.via}\n`;
        if (med.duracion) texto += `   Duración: ${med.duracion}\n`;
        if (med.indicaciones) texto += `   Indicaciones: ${med.indicaciones}\n`;
        texto += '\n';
      });
    }

    if (receta.notas) {
      texto += `*Notas:*\n${receta.notas}`;
    }

    const url = `https://wa.me/?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank');
  }

  // ==================== PLANTILLAS ====================
  abrirModalPlantillas(): void {
    this.modalPlantillasAbierto = true;
  }

  cerrarModalPlantillas(): void {
    this.modalPlantillasAbierto = false;
  }

  iniciarCreacionPlantilla(): void {
    this.plantillaEnEdicion = null;
    this.formPlantilla = { nombre: '', descripcion: '' };
    this.modalFormPlantillaAbierto = true;
  }

  editarPlantilla(plantillaId: string): void {
    const plantilla = this.plantillasRecetas.find((p) => p.id === plantillaId);
    if (!plantilla) return;

    this.plantillaEnEdicion = plantilla;
    this.formPlantilla = {
      nombre: plantilla.nombre,
      descripcion: plantilla.descripcion || '',
    };
    this.modalFormPlantillaAbierto = true;
  }

  cerrarFormPlantilla(): void {
    this.modalFormPlantillaAbierto = false;
    this.plantillaEnEdicion = null;
    this.formPlantilla = { nombre: '', descripcion: '' };
  }

  guardarFormPlantilla(): void {
    if (!this.formPlantilla.nombre.trim()) {
      return;
    }

    if (this.plantillaEnEdicion) {
      // Editar plantilla existente
      const plantillaActualizada: IPlantillaReceta = {
        ...this.plantillaEnEdicion,
        nombre: this.formPlantilla.nombre.trim(),
        descripcion: this.formPlantilla.descripcion.trim(),
        ultima_modificacion: new Date().toISOString(),
      };
      this.onUpdatePlantilla.emit(plantillaActualizada);
    } else {
      // Crear nueva plantilla
      const receta = this.recetaActual;
      if (!receta || !receta.medicamentos || receta.medicamentos.length === 0) {
        alert('No hay medicamentos para guardar en la plantilla');
        return;
      }

      const nuevaPlantilla: IPlantillaReceta = {
        id: Date.now().toString(),
        nombre: this.formPlantilla.nombre.trim(),
        descripcion: this.formPlantilla.descripcion.trim(),
        medicamentos: JSON.parse(JSON.stringify(receta.medicamentos)), // Deep copy
        fecha_creacion: new Date().toISOString(),
        ultima_modificacion: new Date().toISOString(),
      };

      this.onSavePlantilla.emit(nuevaPlantilla);
    }

    this.cerrarFormPlantilla();
  }

  aplicarPlantilla(plantillaId: string): void {
    const plantilla = this.plantillasRecetas.find((p) => p.id === plantillaId);
    if (!plantilla) return;

    const receta = this.recetaActual;
    if (!receta || receta.finalizada) return;

    // Copiar medicamentos de la plantilla a la receta actual
    receta.medicamentos = JSON.parse(JSON.stringify(plantilla.medicamentos)); // Deep copy
    receta.ultima_modificacion = new Date().toISOString();

    this.emitirCambios();
    this.cerrarModalPlantillas();

    console.log('[Indicaciones] Plantilla aplicada:', plantilla.nombre);
  }

  eliminarPlantilla(plantillaId: string): void {
    if (confirm('¿Estás seguro de eliminar esta plantilla?')) {
      this.onDeletePlantilla.emit(plantillaId);
    }
  }

  // ==================== TABS ====================
  selectTab(index: number): void {
    console.log('[Indicaciones] ===== selectTab START =====');
    console.log('[Indicaciones] index recibido:', index);
    console.log('[Indicaciones] indicacionesMedicas.length:', this.indicacionesMedicas?.length);
    console.log('[Indicaciones] activeIndicacionIndex ANTES:', this.activeIndicacionIndex);

    this.activeIndicacionIndex = index;

    console.log('[Indicaciones] activeIndicacionIndex DESPUES:', this.activeIndicacionIndex);

    const recetaSeleccionada = this.indicacionesMedicas[index];
    console.log('[Indicaciones] Receta en index:', recetaSeleccionada?.nombre);
    console.log('[Indicaciones] Receta finalizada:', recetaSeleccionada?.finalizada);
    console.log('[Indicaciones] Receta medicamentos:', recetaSeleccionada?.medicamentos?.length, 'items');
    console.log('[Indicaciones] medicamentosActivos getter:', this.medicamentosActivos?.length);
    console.log('[Indicaciones] ===== selectTab END =====');

    this.cdr.detectChanges();
  }

  // TrackBy para mejorar rendimiento de ngFor
  trackByIndex(index: number, item: any): number {
    return index;
  }

  openOverflowIndicaciones(): void {
    this.overflowIndicacionesOpen = true;
  }

  closeOverflowIndicaciones(): void {
    this.overflowIndicacionesOpen = false;
  }

  selectIndicacionFromOverflow(realIndex: number): void {
    // Actualizar el indice activo
    this.activeIndicacionIndex = realIndex;
    // Cerrar el modal
    this.overflowIndicacionesOpen = false;
    // Forzar deteccion de cambios
    this.cdr.detectChanges();
  }

  // ==================== EMITIR CAMBIOS AL PADRE ====================
  emitirCambios(): void {
    this.onSave.emit([...this.indicacionesMedicas]);
  }

  // ==================== GETTERS ====================
  get recetaActual(): IRecetaSolicitud | null {
    if (this.activeIndicacionIndex === null) return null;
    return this.indicacionesMedicas[this.activeIndicacionIndex] || null;
  }

  get medicamentosActivos(): IMedicamentoReceta[] {
    const receta = this.recetaActual;
    if (!receta) {
      return [];
    }
    if (!receta.medicamentos) {
      receta.medicamentos = [];
    }
    return receta.medicamentos;
  }

  get tieneRecetasNoHistoricas(): boolean {
    return this.indicacionesMedicas.some(r => !r.esHistorico);
  }

  get recetaActualNoHistorica(): boolean {
    if (this.activeIndicacionIndex === null) return false;
    const receta = this.indicacionesMedicas[this.activeIndicacionIndex];
    return receta && !receta.esHistorico;
  }

  get tieneHistorial(): boolean {
    return this.indicacionesMedicas.some(r => r.esHistorico);
  }

  get visibleIndicaciones(): Array<{ item: IRecetaSolicitud; index: number }> {
    const todas = this.indicacionesMedicas.map((item, index) => ({ item, index }));

    // Ordenar por fecha descendente (más recientes primero - izquierda a derecha)
    const ordenadas = [...todas].sort((a, b) => {
      const dateA = a.item.fecha_creacion ? new Date(a.item.fecha_creacion).getTime() : 0;
      const dateB = b.item.fecha_creacion ? new Date(b.item.fecha_creacion).getTime() : 0;
      return dateB - dateA;
    });

    // Retornar solo las primeras tabsLimit (las más recientes)
    return ordenadas.slice(0, this.tabsLimit);
  }

  get overflowCount(): number {
    return Math.max(0, this.indicacionesMedicas.length - this.tabsLimit);
  }

  get overflowIndicaciones(): Array<{ item: IRecetaSolicitud; index: number }> {
    // Mapear manteniendo el indice original para que selectTab funcione correctamente
    const mapped = this.indicacionesMedicas.map((it, i) => ({ item: it, index: i }));
    // Ordenar por fecha_creacion descendente (mas recientes primero)
    return mapped.sort((a, b) => {
      const dateA = new Date(a.item.fecha_creacion).getTime();
      const dateB = new Date(b.item.fecha_creacion).getTime();
      return dateB - dateA;
    });
  }

  // ==================== API PÚBLICA ====================
  getData(): IRecetaSolicitud[] {
    return this.indicacionesMedicas;
  }
}
